import { AlarmConfigState, type AlarmContactType, type AlarmMode } from 'clients/CallhomeApiTypes';
import camelCase from 'lodash/camelCase';
import each from 'lodash/each';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import AuthService from 'models/AuthService';
import moment from 'moment-timezone';
import * as CoreAnalytics from 'src/core/analytics';
import type {
  InvestigationStatus,
  InvestigationVisibility,
} from 'src/hooks/investigations/useInvestigations';
import { distanceInMBetweenEarthCoordinates, getUserLocationDetails } from 'utils/GeolocationUtil';
import {
  CAMERA_GROUP_TYPE_FLOORPLAN_LOWERCASE,
  CAMERA_GROUP_TYPE_STANDARD,
  CAMERA_GROUP_TYPE_STANDARD_LOWERCASE,
  EXCLUDE_FROM_COUNT_TAG,
  HIDE_FROM_DISPLAY_TAG,
  MOTION_TYPES,
  OWNER_TYPE,
  type PinType,
  type Status,
  TAB_VIDEO,
  type TypeOfAlarmException,
  VISIBILITY_PRIVATE,
  VISIBILITY_SHARED,
} from 'vd-constants';
import {
  AUDIT_EVENT_BROWSER,
  AUDIT_EVENT_CITY,
  AUDIT_EVENT_COUNTRY,
  AUDIT_EVENT_OS,
  TAB_NAME,
  TRACKING_EVENTS,
  VIDEO_SEARCH_MEDIA_METHOD,
  VIDEO_SEARCH_MEDIA_TYPE,
} from './AnalyticsConstants';

import { type DerivedStream } from '@solink/vd-data-layer/api-clients/callhome/callhome';
import { type FieldStatsContext } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventTrackingZone } from '@solink/vd-data-layer/api-clients/vms/vmsTypes';
import {
  type CallhomeCameraGroup,
  type CameraLinkId,
  type CHCameraId,
  type CustomerId,
  type DeviceId,
  type SiteId,
  type UserId,
} from '@solinkcorp/api-types';
import { type EventStandardDefs, type Field } from '@solinkcorp/event-standard';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { type CallHomeWidget, EVENT_TYPE_BOOKMARK, type Threshold } from '@solinkcorp/vd-common';
import { PlatformApi } from 'clients';
import { type IEvent } from 'clients/PlatformApi';
import { type AIInspectionDeviceType } from 'components/ScheduledActivityForm/ScheduledActivityForm';
import { type Precision } from 'components/SidekickPage/constants';
import { type TimelineEvent } from 'components/VideoPlayer/components/VideoHistogram/types';
import { trackUser } from 'src/core/analytics/mp';
import { LocationStore } from 'stores/LocationStore';
import type { ValueOf } from 'type-fest';
import { getPinTypeLabel } from 'utils/AlarmContactUtils';
import { type AlarmMonitoringMode } from 'utils/AlarmUtil';
import * as EventUtil from 'utils/EventUtil';
import { type PlayerType } from 'utils/PlayerUtil';
import type ReportUtil from 'utils/ReportUtil';
import SystemReport from 'utils/SystemReport';
import {
  type CamerasDictionaryCamera,
  type VideoDiscoveryCameraGroup,
  type VideoDiscoverySite,
} from 'utils/types';

type Settings = Record<string, unknown>;

interface FlattenPropsArgs<P extends string, V extends Record<string, unknown>> {
  prop: P;
  values: V;
}

function flattenProps<T extends FlattenPropsArgs<string, Record<string, unknown>>>(args: T) {
  const { prop, values } = args;

  type P = T['prop'];
  type V = T['values'];

  // Create an empty object with the intended output type.
  const result = {} as {
    [K in keyof V as `${P}${Capitalize<string & K>}`]: V[K];
  };

  each(values, (value, key) => {
    const newKey = camelCase(`${prop}-${key}`) as unknown as `${P}${Capitalize<string & keyof V>}`;

    (result as Record<string, unknown>)[newKey] = value;
  });

  return result;
}

const ALARM_CONTACT_TYPE_AUDIT_LABELS: Partial<Record<AlarmContactType, string>> = {
  STANDARD: 'SOLINK USER',
  EXTERNAL: 'NON SOLINK USER',
};

/** Maps STANDARD/EXTERNAL to customer-facing labels for audit-log and Mixpanel clarity. */
export const formatAlarmContactType = (type?: AlarmContactType): string | undefined =>
  type ? (ALARM_CONTACT_TYPE_AUDIT_LABELS[type] ?? type) : undefined;

export const formatAlarmContactPinType = (
  pinType: PinType | null | undefined,
  locale: LocaleDefinition,
): string => getPinTypeLabel(pinType, locale);

const formatEventObject = ({ event }: { event?: IEvent | TimelineEvent }) => {
  const defaultValues = {
    id: '',
    type: '',
    title: '',
    subtitle: '',
  };

  if (!event) {
    return defaultValues;
  }

  const id = 'id' in event ? event.id : defaultValues.id;
  const type = 'type' in event ? event.type : defaultValues.type;
  const title = 'title' in event ? event.title : defaultValues.title;
  const subtitle = 'subtitle' in event ? event.subtitle : defaultValues.subtitle;

  return {
    id,
    type,
    title,
    subtitle,
  };
};

const formatLocationObject = ({ location }: { location?: { id: string; name: string } }) => {
  const { id, name } = location ?? {};

  return {
    id,
    name,
  };
};

type MinimalGroup = {
  cameras?: unknown[];
  cameraIds?: unknown[];
  id?: string;
  name?: string;
  ownerType?: string;
  type?: string;
};

const formatGroupObject = (group?: MinimalGroup | unknown) => {
  if (!group) {
    return {};
  }

  const groupObject = group as {
    cameras?: unknown[];
    cameraIds?: unknown[]; // TODO: should be updated to proper ID type when passed in (ie. could be CHCameraId[])
    id?: string;
    name?: string;
    ownerType?: string;
    type?: string;
  };

  const { cameras = [], cameraIds = [], id, name, ownerType, type } = groupObject;
  const isShared = ownerType === OWNER_TYPE.CUSTOMER || !ownerType;
  const isGroupToCreate = !id && !type;
  const isStandard = type === CAMERA_GROUP_TYPE_STANDARD || isGroupToCreate;

  return {
    id,
    name,
    numberOfCameras: cameras.length,
    visibility: isShared ? VISIBILITY_SHARED : VISIBILITY_PRIVATE,
    totalNumberOfCameras: cameraIds.length,
    type: isStandard ? CAMERA_GROUP_TYPE_STANDARD_LOWERCASE : CAMERA_GROUP_TYPE_FLOORPLAN_LOWERCASE,
  };
};

const formatCameraObject = ({ camera }: { camera: { id: string; name: string } }) => {
  const { id, name } = camera;

  return {
    id,
    name,
  };
};

const formatEventView = ({ isTableView }: { isTableView: boolean }) => {
  return {
    viewMode: isTableView ? 'Table View' : 'Event Grid',
  };
};

const formatEventTypeFilterObject = (filter: {
  starred?: boolean;
  favorites?: boolean;
  saved?: boolean;
}) => {
  const defaultState = {
    alarm: false,
    bookmark: false,
    favorites: false,
    motion: false,
    transaction: false,
  };
  const { starred } = filter;

  delete filter.saved;
  if (typeof starred !== 'undefined') {
    filter.favorites = starred;
    delete filter.starred;
  }

  return {
    ...defaultState,
    ...filter,
  };
};

const formatHourInterval = ({ hours }: { hours: number }) => {
  return isNil(hours) ? undefined : `${hours} hour(s)`;
};

/** Screen that started the create flow. */
export type InvestigationCreateSource = 'list' | 'add-to-investigation';

/** Screen that changed the evidence of an investigation. */
export type InvestigationEvidenceSource = 'add-to-investigation' | 'investigation-detail';

export type ZoneMaxOccupancyChangeType = 'enabled' | 'disabled' | 'modified';

export type ZoneMaxOccupancyChangedEventProperties = {
  cameraId: string;
  cameraName?: string;
  locationId: string;
  saveBatchId: string;
  saveBatchZoneCount: number;
  changeType: ZoneMaxOccupancyChangeType;
  maxOccupancyEnabled: boolean;
  maxOccupancyCount?: number;
  maxOccupancyGracePeriodMs?: number;
};

type MaxOccupancyMetadata = {
  isMaxOccupancyEnabled?: boolean;
  maxOccupancyCount?: number;
  maxOccupancyGracePeriodMs?: number;
};

/**
 * Pure helper — builds the Mixpanel property payload for each max-occupancy
 * state transition detected between the old and new zone snapshots.
 */
export function buildZoneMaxOccupancyChangedEvents({
  cameraId,
  cameraName,
  locationId,
  saveBatchId,
  oldZones,
  newZones,
}: {
  cameraId: string;
  cameraName?: string;
  locationId: string;
  saveBatchId: string;
  oldZones: EventTrackingZone[];
  newZones: EventTrackingZone[];
}): ZoneMaxOccupancyChangedEventProperties[] {
  // polygonId is used internally to correlate old vs new snapshots; it is not
  // part of the emitted payload.
  const getPolygonId = (z: EventTrackingZone): string | undefined => z.metadata?.polygonId;
  const getMd = (z: EventTrackingZone): MaxOccupancyMetadata | undefined =>
    z.metadata as MaxOccupancyMetadata | undefined;
  const isEnabled = (z: EventTrackingZone): boolean => !!getMd(z)?.isMaxOccupancyEnabled;

  const oldById = new Map<string, EventTrackingZone>();
  for (const z of oldZones) {
    const id = getPolygonId(z);
    if (id !== undefined) oldById.set(id, z);
  }
  const newById = new Map<string, EventTrackingZone>();
  for (const z of newZones) {
    const id = getPolygonId(z);
    if (id !== undefined) newById.set(id, z);
  }

  const saveBatchZoneCount = newZones.length;

  const shape = (
    sourceZone: EventTrackingZone,
    changeType: ZoneMaxOccupancyChangeType,
  ): ZoneMaxOccupancyChangedEventProperties => {
    const md = getMd(sourceZone);
    // Only `enabled` and `modified` emit count/grace — both describe an ON state,
    // sourced from the *new* zone. `disabled` intentionally drops old values.
    const maxOccEnabled = changeType !== 'disabled';
    return {
      cameraId,
      cameraName,
      locationId,
      saveBatchId,
      saveBatchZoneCount,
      changeType,
      maxOccupancyEnabled: maxOccEnabled,
      maxOccupancyCount: maxOccEnabled ? md?.maxOccupancyCount : undefined,
      maxOccupancyGracePeriodMs: maxOccEnabled ? md?.maxOccupancyGracePeriodMs : undefined,
    };
  };

  const events: ZoneMaxOccupancyChangedEventProperties[] = [];

  for (const [id, nz] of newById) {
    const oz = oldById.get(id);
    const newEnabled = isEnabled(nz);
    const oldEnabled = oz ? isEnabled(oz) : false;

    if (!oldEnabled && newEnabled) {
      // Covers both "zone didn't exist" and "existed but was off" → now on.
      events.push(shape(nz, 'enabled'));
    } else if (oldEnabled && !newEnabled) {
      events.push(shape(nz, 'disabled'));
    } else if (oldEnabled && newEnabled && oz) {
      const oMd = getMd(oz);
      const nMd = getMd(nz);
      const countChanged = oMd?.maxOccupancyCount !== nMd?.maxOccupancyCount;
      const graceChanged = oMd?.maxOccupancyGracePeriodMs !== nMd?.maxOccupancyGracePeriodMs;
      if (countChanged || graceChanged) {
        events.push(shape(nz, 'modified'));
      }
    }
  }
  for (const [id, oz] of oldById) {
    // Zone deleted while max-occ was on → disabled. shape() drops count/grace.
    if (!newById.has(id) && isEnabled(oz)) {
      events.push(shape(oz, 'disabled'));
    }
  }

  return events;
}

// TODO: Future - change all AnalyticsLogger methods to be async
class AnalyticsLogger {
  private location: Promise<{
    country: string;
    city: string;
    latitude: number;
    longitude: number;
  }>;
  private superProperties?: Record<string, unknown>;
  private eventStandard?: EventStandardDefs;

  constructor() {
    // This is asynchronous: must await this.location before referencing it
    this.location = getUserLocationDetails();
  }

  /**
   * Called to set eventStandard and superProperties when these values change.
   */
  setup(eventStandard?: EventStandardDefs, superProperties?: Record<string, unknown>) {
    this.superProperties = superProperties;
    this.eventStandard = eventStandard;
  }

  async createAuditEvent({
    name,
    properties,
    auditEventSubtype,
    description,
  }: {
    name: string;
    properties?: {
      locationId?: string;
      locationName?: string;
      [key: string]: unknown;
    };
    auditEventSubtype?: string;
    description?: string;
  }) {
    const sr = new SystemReport();
    const { country, city, latitude, longitude } = await this.location;

    /*
     * These properties are NOT sent to mixpanel because they are acutally provided by mixpanel
     * We do however want them to be apart of the audit events, so we need to determine what these are
     */
    const auditEventDefaultDetails: {
      os: null;
      browser: null;
      city?: string;
      country?: string;
    } = {
      [AUDIT_EVENT_OS]: sr.report.os.name,
      [AUDIT_EVENT_BROWSER]: sr.report.browser.name,
    };

    if (city) auditEventDefaultDetails[AUDIT_EVENT_CITY] = city;
    if (country) auditEventDefaultDetails[AUDIT_EVENT_COUNTRY] = country;

    /*
     * Merge the default properties with superproperties and the properties specific to the audit event
     */
    const mergedProperties: { [key: string]: unknown } = {
      ...auditEventDefaultDetails,
      ...this.superProperties,
      ...properties,
    };

    const auditEventDetails: { [key: string]: unknown } = { eventType: name };

    /*
     * Here we are referencing event standard to determine which properties should be included in the audit event
     * Audit events are essentially partial mixpanel/intercom events
     * There are certain properties which we want to send to mixpanel and intercom, but not included in the audit event
     */
    for (const property in mergedProperties) {
      if (this.eventStandard?.[`details.auditLog.${property}`]) {
        auditEventDetails[property] = mergedProperties[property];
      }
    }
    const eventType = this.eventStandard?.['details.auditLog.eventType']?.values?.[name] ?? name;
    const visibilityScope = EventUtil.getVisibilityScopeAuditEvents();

    const location = LocationStore.getLocation(
      (properties?.siteId ?? properties?.locationId) as SiteId,
    );

    const startDate = moment();

    const event = {
      isRealtime: true,
      isAudit: true,
      startTime: startDate.utc().format(),
      startTimeLocal: startDate.tz(location?.timezone || getLocalTimeZone()).format(),
      customerId: AuthService.getSession()?.tenantId || null,
      title: eventType,
      type: EVENT_TYPE_BOOKMARK,
      subtype: auditEventSubtype,
      details: {
        auditLog: auditEventDetails,
        description,
      } as {
        auditLog: { [key: string]: unknown };
        gpsCoordinates?: {
          lng: number;
          lat: number;
          distanceFromLocation: number;
        };
      },
      creator: this.superProperties?.auth0Id,
      created: startDate.utc().format(),
      locationId: properties?.locationId,
      siteId: properties?.siteId || properties?.locationId,
      subtitle: `AUDIT @ ${properties?.locationName}`,
      tags: [HIDE_FROM_DISPLAY_TAG, EXCLUDE_FROM_COUNT_TAG],
      scope: {
        visibility: visibilityScope,
      },
    };

    if (latitude && longitude && location?.geolocation?.lat && location?.geolocation?.lng) {
      const distanceFromLocation = distanceInMBetweenEarthCoordinates({
        lat1: latitude,
        lon1: longitude,
        lat2: location.geolocation.lat,
        lon2: location.geolocation.lng,
      });
      event.details.gpsCoordinates = {
        lng: longitude,
        lat: latitude,
        distanceFromLocation,
      };
    }

    try {
      await PlatformApi().createEventIngest(event);
    } catch (error) {
      console.error(error);
    }
  }

  shouldCreateAuditEvent(name: string) {
    const auditEventTypes = this.eventStandard?.['details.auditLog.eventType']?.values;
    return !!auditEventTypes?.[name];
  }

  trackEvent(event: {
    name: string;
    properties?: { locationId?: string; locationName?: string; [key: string]: unknown };
    auditEventSubtype?: string;
    description?: string;
  }) {
    if (this.shouldCreateAuditEvent(event.name)) {
      this.createAuditEvent(event);
    }

    CoreAnalytics.trackEvent(event);
  }

  selectLocationOnVideoPage({ location }: { location: { id: string; name: string } }) {
    this.trackEvent({
      ...TRACKING_EVENTS.LOCATION_SELECT_VIDEO,
      properties: {
        ...formatLocationObject({ location }),
        numberOfCameras: get(location, 'cameras', []).length,
      },
    });
  }

  selectCameraGroupOnVideoPage(group: VideoDiscoveryCameraGroup) {
    this.trackEvent({
      ...TRACKING_EVENTS.SELECT_CAMERA_GROUP,
      properties: {
        ...formatGroupObject(group),
        tab: TAB_VIDEO,
      },
    });
  }

  addFavouriteCamera({ camera }: { camera: { id: string; name: string } }) {
    this.trackEvent({
      ...TRACKING_EVENTS.FAVOURITE_CAMERA_ADD,
      properties: {
        ...formatCameraObject({ camera }),
      },
    });
  }

  removeFavouriteCamera({ camera }: { camera: { id: string; name: string } }) {
    this.trackEvent({
      ...TRACKING_EVENTS.FAVOURITE_CAMERA_REMOVE,
      properties: {
        ...formatCameraObject({ camera }),
      },
    });
  }

  selectNavigationTab({ tab }: { tab: string }) {
    const tabName = TAB_NAME[tab as keyof typeof TAB_NAME];

    if (tabName) {
      this.trackEvent({
        ...TRACKING_EVENTS.NAVIGATION_TAB_SELECT,
        properties: {
          tabName,
        },
      });
    } else {
      console.error('Analytics: Unknown navigation tab selected');
    }
  }

  // NOT USED
  dragVideoTimeline() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_TIMELINE_DRAG });
  }

  dragVideoViewport() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_VIEWPORT_DRAG });
  }

  downloadSnapshot() {
    this.trackEvent({ ...TRACKING_EVENTS.SNAPSHOT_DOWNLOAD });
  }

  addCameraToMultiview({ camera }: { camera: { id: string; name: string } }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MULTIVIEW_VIDEO_CAMERA_ADD,
      properties: {
        ...formatCameraObject({ camera }),
      },
    });
  }

  removeCameraFromMultiview({ camera }: { camera: { id: string; name: string } }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MULTIVIEW_VIDEO_CAMERA_REMOVE,
      properties: {
        ...formatCameraObject({ camera }),
      },
    });
  }

  fastForwardVideo({ speed }: { speed: number }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_FAST_FORWARD,
      properties: {
        speed,
      },
    });
  }

  zoomVideo({ level }: { level: number }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_ZOOM,
      properties: {
        zoomLevel: level,
      },
    });
  }

  cameraLinkToggle({ showingCameraLinks }: { showingCameraLinks: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CANERA_LINK_TOGGLE,
      properties: {
        showingCameraLinks,
      },
    });
  }

  cameraLinkClick({
    cameraLinkId,
    centerX,
    centerY,
    fromCameraId,
    fromCameraName,
    toCameraId,
    toCameraName,
    cameraLinkType,
  }: {
    cameraLinkId: CameraLinkId;
    fromCameraId: CHCameraId;
    fromCameraName: string;
    toCameraId: string; // TODO: should be updated to proper ID type when passed in (ie. could be CHCameraId)
    toCameraName: string;
    centerX: number;
    centerY: number;
    cameraLinkType: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_LINK_CLICK,
      properties: {
        cameraLinkId,
        centerX,
        centerY,
        fromCameraId,
        fromCameraName,
        toCameraId,
        toCameraName,
        cameraLinkType,
      },
    });
  }

  cameraLinkUpdate({
    roi,
    enabled,
    cameraLinkId,
    centerX,
    centerY,
    fromCameraId,
    fromCameraName,
    toCameraId,
    toCameraName,
    cameraLinkType,
  }: {
    roi: { x: number; y: number; width: number; height: number };
    enabled: boolean;
    cameraLinkId: CameraLinkId;
    centerX: number;
    centerY: number;
    fromCameraId: CHCameraId;
    fromCameraName: string;
    toCameraId: CHCameraId;
    toCameraName: CamerasDictionaryCamera;
    cameraLinkType: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CANERA_LINK_UPDATE,
      properties: {
        roi,
        enabled,
        cameraLinkId,
        centerX,
        centerY,
        fromCameraId,
        fromCameraName,
        toCameraId,
        toCameraName,
        cameraLinkType,
      },
    });
  }

  cameraLinkCreate({
    fromCameraId,
    fromCameraName,
    toCameraId,
    toCameraName,
  }: {
    fromCameraId: CHCameraId;
    fromCameraName: string;
    toCameraId: CHCameraId;
    toCameraName: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_LINK_CREATE,
      properties: {
        fromCameraId,
        fromCameraName,
        toCameraId,
        toCameraName,
      },
    });
  }

  cameraLinkDelete({
    cameraLinkType,
    enabled,
    cameraLinkId,
    centerX,
    centerY,
    fromCameraId,
    fromCameraName,
    toCameraId,
    toCameraName,
  }: {
    cameraLinkType: string;
    enabled: boolean;
    cameraLinkId: CameraLinkId;
    centerX: number;
    centerY: number;
    fromCameraId: CHCameraId;
    fromCameraName: string;
    toCameraId: CHCameraId;
    toCameraName: CamerasDictionaryCamera;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_LINK_DELETE,
      properties: {
        cameraLinkId,
        centerX,
        centerY,
        fromCameraId,
        fromCameraName,
        toCameraId,
        toCameraName,
        cameraLinkType,
        enabled,
      },
    });
  }

  viewHeatmap({ isEnabled, hourInterval }: { isEnabled: boolean; hourInterval: number }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_HEATMAP,
      properties: {
        viewHeatmap: !!isEnabled,
        heatmapTimeInterval: isEnabled ? formatHourInterval({ hours: hourInterval }) : '',
      },
    });
  }

  viewMotionSearch({ isEnabled, motionType }: { isEnabled: boolean; motionType: string }) {
    const searchAi =
      {
        [MOTION_TYPES.PERSON]: 'People',
        [MOTION_TYPES.REGULAR]: 'N/A',
        [MOTION_TYPES.VEHICLE]: 'Vehicle',
      }[motionType] || 'N/A';

    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_MOTION_SEARCH,
      properties: {
        motionSearch: !!isEnabled,
        motionType,
        searchAi,
      },
    });
  }

  editMotionZone({ name, email, context }: { name?: string; email: string; context: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EDIT_MOTION_ZONE,
      properties: {
        name,
        email,
        context,
      },
    });
  }

  trackUseMotionSearchDurationThreshold(minDuration: number) {
    this.trackEvent({
      ...TRACKING_EVENTS.USE_MOTION_SEARCH_DURATION_THRESHOLD,
      properties: {
        minDuration,
      },
    });
  }

  trackUseMotionSearchIntensityThreshold(minIntensity: number) {
    this.trackEvent({
      ...TRACKING_EVENTS.USE_MOTION_SEARCH_INTENSITY_THRESHOLD,
      properties: {
        minIntensity,
      },
    });
  }

  searchVideo({ searchTerm }: { searchTerm: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_SEARCH_TERM,
      properties: {
        searchTerm,
      },
    });
  }

  selectMotionAlerts({
    motionRecordingEnabled,
    cameraName,
    locationName,
    locationId,
    numberOfGridSpacesHighlighted,
    siteId,
    deviceId,
  }: {
    motionRecordingEnabled: boolean;
    cameraName: string;
    locationName?: string;
    locationId: string; // TODO: should be updated to proper ID type when passed in (ie. could be SiteId)
    numberOfGridSpacesHighlighted: number;
    siteId: SiteId | string; // TODO: should be updated to proper ID type when passed in (ie. should be SiteId)
    deviceId: DeviceId | string; // TODO: should be updated to proper ID type when passed in (ie. should be DeviceId)
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MOTION_ALERTS_SELECTED,
      properties: {
        motionRecordingEnabled,
        cameraName,
        locationName,
        locationId,
        numberOfGridSpacesHighlighted,
        siteId,
        deviceId,
      },
    });
  }

  motionAlertGridReset({
    cameraName,
    locationName,
    locationId,
    siteId,
    deviceId,
  }: {
    cameraName: string;
    locationName: string;
    locationId: SiteId;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MOTION_ALERT_GRID_RESET,
      properties: {
        cameraName,
        locationName,
        locationId,
        siteId,
        deviceId,
      },
    });
  }

  motionAlertGridErase({
    cameraName,
    locationName,
    locationId,
    numberOfGridSpacesHighlighted,
    siteId,
    deviceId,
  }: {
    cameraName: string;
    locationName: string;
    locationId: SiteId;
    numberOfGridSpacesHighlighted: number;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MOTION_ALERT_GRID_ERASE,
      properties: {
        cameraName,
        locationName,
        locationId,
        numberOfGridSpacesHighlighted,
        siteId,
        deviceId,
      },
    });
  }

  motionAlertGridHighlight({
    cameraName,
    locationName,
    locationId,
    numberOfGridSpacesHighlighted,
    siteId,
    deviceId,
  }: {
    cameraName: string;
    locationName: string;
    locationId: SiteId;
    numberOfGridSpacesHighlighted: number;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MOTION_ALERT_GRID_HIGHLIGHT,
      properties: {
        cameraName,
        locationName,
        locationId,
        numberOfGridSpacesHighlighted,
        siteId,
        deviceId,
      },
    });
  }

  setMotionRoi({
    saveSuccessful,
    error,
    motionRecordingEnabled,
    cameraName,
    locationName,
    locationId,
    numberOfGridSpacesHighlighted,
    previousNumberOfGridSpacesHighlighted,
    siteId,
    deviceId,
  }: {
    cameraName: string;
    error?: string;
    motionRecordingEnabled?: boolean;
    locationName: string;
    locationId: SiteId;
    numberOfGridSpacesHighlighted: number;
    previousNumberOfGridSpacesHighlighted: number;
    saveSuccessful: boolean;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SET_MOTION_ROI,
      properties: {
        saveSuccessful,
        ...(error ? { error } : {}),
        motionRecordingEnabled,
        cameraName,
        locationName,
        locationId,
        numberOfGridSpacesHighlighted,
        previousNumberOfGridSpacesHighlighted,
        siteId,
        deviceId,
      },
    });
  }

  motionAlertAiDetectionTypeSaved({
    cameraName,
    error = undefined,
    detectionType,
    locationName,
    locationId,
    saveSuccessful,
    siteId,
    deviceId,
  }: {
    cameraName: string;
    error?: string;
    detectionType: string;
    locationName: string;
    locationId: string;
    saveSuccessful: boolean;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.MOTION_ALERT_AI_DETECTION_TYPE_SAVED,
      properties: {
        cameraName,
        detectionType,
        ...(error ? { error } : {}),
        locationName,
        locationId,
        saveSuccessful,
        siteId,
        deviceId,
      },
    });
  }

  /**
   *
   * @param {ReportUtil|null} report
   * @param {string} locale
   */
  selectReportOnEventsPage({ report, locale }: { report: ReportUtil; locale: string }) {
    let reportName = 'All Events';
    let reportTemplateId = null;
    let reportVersion = null;
    if (report) {
      reportName = report.getName(locale);
      reportTemplateId = report.getTemplateId();
      reportVersion = report.getReportVersion();

      // Track whenever a user views a report
      trackUser({
        incrementProperties: {
          numberOfReportViews: 1,
        },
        properties: {},
      });
    }

    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_REPORT_SELECT,
      properties: {
        reportName,
        reportTemplateId,
        reportVersion,
      },
    });
  }

  addSearch({ searchTerm }: { searchTerm: string }) {
    // TODO "snapshot" of search params after change (expected to be easier after lightsaber)
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_SEARCH_ADD,
      properties: {
        searchTerm,
      },
    });
  }

  removeSearch({ searchTerm }: { searchTerm: string }) {
    // TODO "snapshot" of search params after change (expected to be easier after lightsaber)
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_SEARCH_REMOVE,
      properties: {
        searchTerm,
      },
    });
  }

  addParameterizedSearch({ searchQuery }: { searchQuery: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_PARAMETERIZED_SEARCH_ADD,
      properties: {
        searchQuery,
      },
    });
  }

  removeParameterizedSearch({ searchQuery }: { searchQuery: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_PARAMETERIZED_SEARCH_REMOVE,
      properties: {
        searchQuery,
      },
    });
  }

  createReport({ reportName }: { reportName: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.FILTER_REPORT_CREATE,
      properties: {
        reportName,
      },
    });
  }

  viewEvent({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_VIEW,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  addEventNotes() {
    this.trackEvent({ ...TRACKING_EVENTS.EVENT_ADD_NOTES });
  }

  toggleEventShare({ isShown }: { isShown: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_SHARE_TOGGLE,
      properties: {
        shareToggle: !!isShown,
      },
    });
  }

  clickEventShareCopy({ url }: { url: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_SHARE_COPY,
      properties: {
        url,
      },
    });
  }

  clickEventShareEmail({ mailTo }: { mailTo: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_SHARE_EMAIL,
      properties: {
        mailTo,
      },
    });
  }

  clickEventShareCSVExport() {
    this.trackEvent({ ...TRACKING_EVENTS.EVENT_SHARE_CSV_EXPORT });
  }

  selectDatePeriod({ period }: { period: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_DATE_PERIOD,
      properties: {
        timePeriod: period,
      },
    });
  }

  selectEventLocation({
    location,
  }: {
    location: {
      id: string;
      name: string;
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_LOCATION_SELECT,
      properties: {
        ...formatLocationObject({ location }),
      },
    });
  }

  toggleEventTypeFilter({
    filter,
    range,
    tab,
    featureTab,
  }: {
    filter: { starred?: boolean; favorites?: boolean; saved?: boolean };
    range: string;
    tab?: string;
    featureTab?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_EVENT_TYPE_FILTER,
      properties: {
        ...formatEventTypeFilterObject(filter),
        range: range.toLowerCase(),
        tab,
        featureTab,
      },
    });
  }

  selectHistogramTime({ date }: { date: Date }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_HISTOGRAM_TIME_SELECT,
      properties: {
        time: moment(date).format('hh:mm:ss A'),
        date: new Date(date).toString(),
      },
    });
  }

  addUser({ email, userType, name }: { email: string; userType: string; name: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.USER_ADD,
      properties: {
        email,
        userType,
        name,
      },
    });
  }

  deleteUser({ email }: { email: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.USER_DELETE,
      properties: {
        email,
      },
    });
  }

  changePassword() {
    this.trackEvent({ ...TRACKING_EVENTS.USER_PASSWORD_CHANGE });
  }

  changeSupportAccess({ isEnabled }: { isEnabled: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.USER_SUPPORT_ACCESS_CHANGE,
      properties: {
        supportAccess: !!isEnabled,
      },
    });

    trackUser({
      properties: {
        supportAccess: !!isEnabled,
      },
    });
  }

  toggleDigestNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      properties: {
        isDigestEnabled: !!isEnabled,
      },
    });
  }

  toggleDigestDailyNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      incrementProperties: {
        countDailyDigest: isEnabled ? 1 : -1,
      },
      properties: {
        isDigestDailyEnabled: !!isEnabled,
      },
    });
  }

  toggleDigestWeeklyNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      incrementProperties: {
        countWeeklyDigest: isEnabled ? 1 : -1,
      },
      properties: {
        isDigestWeeklyEnabled: !!isEnabled,
      },
    });
  }

  togglePushNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      incrementProperties: {
        countPushNotifications: isEnabled ? 1 : -1,
      },
      properties: {
        isPushNotificationEnabled: !!isEnabled,
      },
    });
  }

  toggleSMSNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      incrementProperties: {
        countSMSNotifications: isEnabled ? 1 : -1,
      },
      properties: {
        isSMSNotificationEnabled: !!isEnabled,
      },
    });
  }

  toggleEmailNotification({ isEnabled }: { isEnabled: boolean }) {
    trackUser({
      incrementProperties: {
        countEmailNotifications: isEnabled ? 1 : -1,
      },
      properties: {
        isEmailNotificationEnabled: !!isEnabled,
      },
    });
  }

  toggleReportVisibility() {
    // TODO Placeholder - Do nothing for now
  }

  clickReferral() {
    this.trackEvent({ ...TRACKING_EVENTS.REFERRAL_CLICK });
  }

  clickHelpCenter() {
    this.trackEvent({ ...TRACKING_EVENTS.HELPCENTER_CLICK });
  }

  /**
   * Create a new discover chart
   * @param {Object} params - Parameters for the discover chart
   * @param {string} params.name - Name of the event
   * @param {string} params.aggregationType - Type of aggregation
   * @param {string | undefined} params.aggregateBy - Field to aggregate
   * @param {string} params.groupBy - Field to group by
   * @param {Array} params.filterItems - Array of filter items applied to the chart
   */
  createDiscoverChart({
    name,
    aggregationType,
    aggregateBy,
    groupBy,
    filterItems = [],
  }: {
    name: string;
    aggregationType: string;
    aggregateBy?: string;
    groupBy: string;
    filterItems: string[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_CHART_CREATE,
      properties: {
        name,
        aggregationType,
        aggregateBy,
        groupBy,
        filterItems,
        countFilterItems: filterItems.length,
      },
    });
  }

  hideDiscoverChart({ name, reportTemplateId }: { name: string; reportTemplateId: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_CHART_HIDE,
      properties: {
        name,
        reportTemplateId,
      },
    });
  }

  investigateDiscoverChart({
    id,
    name,
    searchKey,
    showTableView,
  }: {
    id: string;
    name: string;
    searchKey: string;
    showTableView: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_CHART_INVESTIGATE,
      properties: {
        id,
        name,
        searchKey,
        ...formatEventView({ isTableView: showTableView }),
      },
    });
  }

  closeDiscoverEventsView() {
    this.trackEvent({ ...TRACKING_EVENTS.DISCOVER_VIEW_CLOSE });
  }

  switchDiscoverEventsView({ showTableView }: { showTableView: boolean }) {
    const { viewMode } = formatEventView({ isTableView: showTableView });

    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_VIEW_SWITCH,
      properties: {
        switchTo: viewMode,
      },
    });
  }

  changeDiscoverTableViewColumns({ columns }: { columns: string[] }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_TABLE_VIEW_COLUMNS_CHANGE,
      properties: {
        columns,
      },
    });
  }

  changeDiscoverTableViewSort({
    columnName,
    sortOrder,
  }: {
    columnName: string;
    sortOrder: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_TABLE_VIEW_SORT_CHANGE,
      properties: {
        columnName,
        sortOrder,
      },
    });
  }

  clickEventMenuShare({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_SHARE,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  clickEventMenuViewShare({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_VIEW_SHARE,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  clickEventMenuChangeNote({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_NOTES_CHANGE,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  clickEventMenuCopy({ url }: { url: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_COPY,
      properties: {
        url,
      },
    });
  }

  clickEventMenuFavourite({ event, isStarred }: { event: IEvent; isStarred: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_FAVOURITE,
      properties: {
        ...formatEventObject({ event }),
        isStarred,
      },
    });
  }

  clickEventMenuDelete({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_MENU_DELETE,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  clickSaveAndShare() {
    this.trackEvent({ ...TRACKING_EVENTS.SAVE_AND_SHARE_CLICK });
  }

  createSaveAndShare({
    event,
    shareCount,
    cameraCount,
    camera360Count,
    cameraIds,
    cameraNames,
    durationInSec,
    isBestQuality,
    isSuccess,
    saveMotionOnlyVideo,
    streamNames,
    videoSources,
    source,
    errorMessage,
    faceblur,
    faceblurDurationInMin,
  }: {
    event: IEvent;
    shareCount: number;
    cameraCount: number;
    camera360Count: number;
    cameraIds: string[]; // TODO: should be updated to proper ID type when passed in (ie. could be CHCameraId[])
    cameraNames: string[];
    durationInSec: number;
    isBestQuality: boolean;
    isSuccess: boolean;
    saveMotionOnlyVideo: boolean;
    streamNames: string[];
    videoSources: string[];
    source: string;
    errorMessage: string;
    faceblur: boolean;
    faceblurDurationInMin: number;
  }) {
    const { startTimeLocal: eventStartTimeLocal } = event;

    this.trackEvent({
      ...TRACKING_EVENTS.SAVE_AND_SHARE_CREATE,
      properties: {
        ...formatEventObject({ event }),
        eventStartTimeLocal,
        durationInSec,
        isBestQuality,
        isSuccess,
        countShares: shareCount,
        countCameras: cameraCount,
        count360Cameras: camera360Count,
        cameraIds,
        cameraNames,
        saveMotionOnlyVideo,
        streamNames,
        videoSources,
        source,
        errorMessage,
        faceblur,
        faceblurDurationInMin,
      },
    });
  }

  uploadClipVideo({
    eventId,
    cameraId,
    streamId,
    cameraName,
    streamName,
    videoSource,
    isSuccess,
    errorMessage = '',
    startTime,
    endTime,
  }: {
    isSuccess: boolean;
    eventId: string | undefined;
    eventTitle?: string | undefined;
    cameraId: CHCameraId | string; // TODO: should be updated to proper ID type in [CameraAddons.tsx] when passed in (ie. should be CHCameraId)
    cameraName: string;
    streamId: string;
    streamName: string;
    videoSource: string | undefined;
    startTime: string | undefined;
    endTime: string | undefined;
    errorMessage?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.UPLOAD_CLIP_VIDEO,
      properties: {
        eventId,
        cameraId,
        streamId,
        cameraName,
        streamName,
        videoSource,
        startTime,
        endTime,
        isSuccess,
        errorMessage,
      },
    });
  }

  retryClipVideoClick({
    eventId,
    eventTitle,
    cameraId,
    streamId,
    cameraName,
    streamName,
    videoSource,
    startTime,
    endTime,
  }: {
    eventId: string | undefined;
    eventTitle: string | undefined;
    cameraId: CHCameraId | string; // TODO: should be updated to proper ID type in [CameraAddons.tsx] when passed in (ie. should be CHCameraId)
    cameraName: string;
    streamId: string;
    streamName: string;
    videoSource: string | undefined;
    startTime: string | undefined;
    endTime: string | undefined;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.RETRY_CLIP_VIDEO_CLICK,
      properties: {
        eventId,
        eventTitle,
        cameraId,
        streamId,
        cameraName,
        streamName,
        videoSource,
        startTime,
        endTime,
      },
    });
  }

  createLocalDownload({
    event,
    cameraCount,
    camera360Count,
    durationInSec,
    isBestQuality,
    isSuccess,
    saveMotionOnlyVideo,
  }: {
    event: IEvent;
    cameraCount: number;
    camera360Count: number;
    durationInSec: number;
    isBestQuality: boolean;
    isSuccess: boolean;
    saveMotionOnlyVideo: boolean;
  }) {
    const { startTimeLocal: eventStartTimeLocal } = event;

    this.trackEvent({
      ...TRACKING_EVENTS.LOCAL_DOWNLOAD_CREATE,
      properties: {
        ...formatEventObject({ event }),
        eventStartTimeLocal,
        durationInSec,
        isBestQuality,
        isSuccess,
        countCameras: cameraCount,
        count360Cameras: camera360Count,
        saveMotionOnlyVideo,
      },
    });
  }

  downloadClip({ download }: { download: object }) {
    // TODO Originally this event only got the streamId, but now it's receiving an object (no idea about the properties in it)
    this.trackEvent({
      ...TRACKING_EVENTS.LOCAL_DOWNLOAD_CLIP_CLICK,
      properties: {
        ...download,
      },
    });
  }

  selectDateViaIncrement({ date }: { date: Date }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DATE_PICKER_INCREMENT,
      properties: {
        selectedDate: new Date(date),
      },
    });
  }

  selectDateViaDecrement({ date }: { date: Date }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DATE_PICKER_DECREMENT,
      properties: {
        selectedDate: new Date(date),
      },
    });
  }

  selectDateViaDatePicker({ date }: { date: Date }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DATE_PICKER_SELECT,
      properties: {
        selectedDate: new Date(date),
      },
    });
  }

  openDatePicker({ isOpen }: { isOpen: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DATE_PICKER_OPEN,
      properties: {
        isOpen,
      },
    });
  }

  toggleAddOnContextine() {
    // TODO Placeholder - Do nothing for now
  }

  toggleFavouriteEvent({
    event,
    isEnabled,
    isTableView = false,
  }: {
    event: IEvent;
    isEnabled: boolean;
    isTableView?: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FAVOURITE_TOGGLE,
      properties: {
        ...formatEventObject({ event }),
        ...formatEventView({ isTableView }),
        isStarred: !!isEnabled,
      },
    });
  }

  editNote({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_NOTES_EDIT,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  clickDiscoverEvent({ event }: { event: IEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DISCOVER_EVENT_CLICK,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  selectDiscoverDatePeriod() {
    // TODO Placeholder - Do nothing for now
    // ORIGINAL: set-discover-date-range
  }

  toggleVideoReceipt({ isEnabled }: { isEnabled: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_RECEIPT_TOGGLE,
      properties: {
        isEnabled,
      },
    });
  }

  eventClicked({ event }: { event: IEvent }) {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_CLICK,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  playMultiview({ cameraNames }: { cameraNames: string[] }) {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed
    this.trackEvent({
      ...TRACKING_EVENTS.MULTIVIEW_PLAY,
      properties: {
        source: cameraNames.join(','),
        cameraCount: cameraNames.length,
      },
    });
  }

  togglePlayPause({ source }: { source: string }) {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed
    // SOURCE: video-click, click-icon
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_PLAY_PAUSE,
      properties: {
        source,
      },
    });
  }

  expandCollapsePlayer({ source }: { source?: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_EXPAND_COLLAPSE,
      properties: {
        source,
      },
    });
  }

  downloadVideo({
    event,
    durationInSec,
    fileName,
    fileSizeMB,
  }: {
    event: IEvent;
    durationInSec: number;
    fileName?: string;
    fileSizeMB?: number;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.LOCAL_DOWNLOAD_FILE_DOWNLOAD,
      properties: {
        ...formatEventObject({ event }),
        duration: durationInSec,
        name: fileName,
        size: fileSizeMB,
      },
    });
  }

  toggleHD() {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed (auto vs manual interaction)
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_HD_TOGGLE });
  }

  playHD() {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed (auto vs manual interaction)
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_HD_PLAY });
  }

  playSD() {
    // Keeping this here for Intercom, will need to re-evaluate for mixpanel if needed (auto vs manual interaction)
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_SD_PLAY });
  }

  toggleFullScreen() {
    // TODO Placeholder - Do nothing for now
    // ORIGINAL: toggle-full-screen
  }

  goToNextEvent({ event }: { event: TimelineEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_NEXT_EVENT_JUMP,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  goToPreviousEvent({ event }: { event: TimelineEvent }) {
    this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_PREVIOUS_EVENT_JUMP,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  goBackOneFrame() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_GO_BACK_ONE_FRAME });
  }

  goForwardOneFrame() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_GO_FORWARD_ONE_FRAME });
  }

  goBack10Seconds() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_GO_BACK_10_SECONDS });
  }

  goForward10Seconds() {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_GO_FORWARD_10_SECONDS });
  }

  rewind(properties: { rate: number }) {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_REWIND, properties });
  }

  fastForward(properties: { rate: number }) {
    this.trackEvent({ ...TRACKING_EVENTS.VIDEO_FAST_FORWARD, properties });
  }

  pinEvent({ source }: { source: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PIN_EVENT,
      properties: {
        source,
      },
    });
  }

  unpinEvent({ source }: { source: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.UNPIN_EVENT,
      properties: {
        source,
      },
    });
  }

  navigate24hrTimeline({ interaction, tab }: { interaction: string; tab: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NAVIGATE_24HR_TIMELINE,
      properties: {
        interaction,
        tab,
      },
    });
  }

  navigateMainTimeline({ interaction, tab }: { interaction: string; tab: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NAVIGATE_MAIN_TIMELINE,
      properties: {
        interaction,
        tab,
      },
    });
  }

  tenMinutesOfVideoContinuouslyPlaying(properties: { source: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TEN_MINUTES_OF_VIDEO_CONTINUOUSLY_PLAYING,
      properties,
    });
  }

  continueWatchingVideo(properties?: { source: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CONTINUE_WATCHING_VIDEO,
      properties,
    });
  }

  notificationDrawerOpened({ userId }: { userId: UserId }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_DRAWER_OPENED,
      properties: {
        userId,
      },
    });
  }

  notificationFilterView({ type, userId }: { type: string; userId: UserId }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_FILTER_VIEW,
      properties: {
        type,
        userId,
      },
    });
  }

  notificationView({
    eventId,
    launchURL,
    notificationId,
    notificationType,
    subtitle,
    title,
    userId,
  }: {
    eventId: string;
    launchURL: string;
    notificationId: string;
    notificationType: string;
    subtitle: string;
    title: string;
    userId: UserId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_VIEW,
      properties: {
        eventId,
        launchURL,
        notificationId,
        notificationType,
        subtitle,
        title,
        userId,
      },
    });
  }

  notificationMarkedRead({
    actionType,
    notificationCount,
    notificationIds,
    userId,
  }: {
    actionType: string;
    notificationCount: number;
    notificationIds: string[];
    userId: UserId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_MARKED_READ,
      properties: {
        actionType,
        notificationCount,
        notificationIds,
        userId,
      },
    });
  }

  notificationMarkedError({
    actionType,
    notificationCount,
    notificationIds,
    userId,
  }: {
    actionType: string;
    notificationCount: number;
    notificationIds: string[];
    userId: UserId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_MARKED_ERROR,
      properties: {
        actionType,
        notificationCount,
        notificationIds,
        userId,
      },
    });
  }

  notificationArchived({
    actionType,
    notificationCount,
    notificationIds,
    userId,
  }: {
    actionType: string;
    notificationCount: number;
    notificationIds: string[];
    userId: UserId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.NOTIFICATION_ARCHIVED,
      properties: {
        actionType,
        notificationCount,
        notificationIds,
        userId,
      },
    });
  }

  tempAccessOpened({ userId }: { userId: UserId }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TEMP_ACCESS_OPENED,
      properties: { userId },
    });
  }

  tempAccessGranted({
    emails,
    expiryDate,
    userId,
    videoAccessStartDate,
    videoAccessEndDate = undefined,
  }: {
    emails: string[];
    expiryDate: string;
    userId: UserId;
    videoAccessStartDate: string;
    videoAccessEndDate?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TEMP_ACCESS_GRANTED,
      properties: {
        emails,
        expiryDate,
        userId,
        videoAccessStartDate,
        videoAccessEndDate,
      },
    });
  }

  tempAccessFailed({
    emails,
    expiryDate,
    userId,
    videoAccessStartDate,
    videoAccessEndDate = undefined,
  }: {
    emails: string[];
    expiryDate: string;
    userId: UserId;
    videoAccessStartDate: string;
    videoAccessEndDate?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TEMP_ACCESS_FAILED,
      properties: {
        emails,
        expiryDate,
        userId,
        videoAccessStartDate,
        videoAccessEndDate,
      },
    });
  }

  loadDashboardPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_DASHBOARD });
  }

  loadEventPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_EVENT });
  }

  loadDiscoverPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_DISCOVER });
  }

  loadVideoPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_VIDEO });
  }

  loadAccountPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_ACCOUNT });
  }

  loadPreferencesPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_PREFERENCES });
  }

  loadNotificationsPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_NOTIFICATIONS });
  }

  loadSettingsPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_SETTINGS });
  }

  loadAppsPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_APPS });
  }

  loadUsersPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_USERS });
  }

  loadEditUserPage({ email }: { email: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PAGE_LOAD_EDIT_USER,
      properties: {
        email,
      },
    });
  }

  loadInvestigationsPage() {
    this.trackEvent({ ...TRACKING_EVENTS.PAGE_LOAD_INVESTIGATIONS });
  }

  loadInvestigationDetailPage({
    status,
    visibility,
    eventCount,
    clipCount,
  }: {
    status: InvestigationStatus;
    visibility: InvestigationVisibility;
    eventCount: number;
    clipCount: number;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PAGE_LOAD_INVESTIGATION_DETAIL,
      properties: {
        status,
        visibility,
        eventCount,
        clipCount,
      },
    });
  }

  investigationCreated({
    visibility,
    hasDescription,
    source,
  }: {
    visibility: InvestigationVisibility;
    hasDescription: boolean;
    source: InvestigationCreateSource;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.INVESTIGATION_CREATED,
      properties: {
        visibility,
        hasDescription,
        source,
      },
    });
  }

  investigationUpdated({
    titleChanged,
    descriptionChanged,
    visibilityChanged,
  }: {
    titleChanged: boolean;
    descriptionChanged: boolean;
    visibilityChanged: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.INVESTIGATION_UPDATED,
      properties: {
        titleChanged,
        descriptionChanged,
        visibilityChanged,
      },
    });
  }

  investigationStatusChanged({
    previousStatus,
    nextStatus,
  }: {
    previousStatus: InvestigationStatus;
    nextStatus: InvestigationStatus;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.INVESTIGATION_STATUS_CHANGED,
      properties: {
        previousStatus,
        nextStatus,
      },
    });
  }

  investigationEvidenceUpdated({
    investigationId,
    source,
    isNewInvestigation,
    eventsAdded,
    eventsRemoved,
    clipsAdded,
    clipsRemoved,
  }: {
    investigationId: string;
    source: InvestigationEvidenceSource;
    isNewInvestigation: boolean;
    eventsAdded: number;
    eventsRemoved: number;
    clipsAdded: number;
    clipsRemoved: number;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.INVESTIGATION_EVIDENCE_UPDATED,
      properties: {
        investigationId,
        source,
        isNewInvestigation,
        eventsAdded,
        eventsRemoved,
        clipsAdded,
        clipsRemoved,
      },
    });
  }

  /** Single-item add. Same Mixpanel event as mobile. clipId is sent only for a clip. */
  clipInvestigationLinked({
    clipId,
    investigationId,
    isNewInvestigation,
  }: {
    clipId?: string;
    investigationId: string;
    isNewInvestigation: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CLIP_INVESTIGATION_LINKED,
      properties: {
        ...(clipId ? { clipId } : {}),
        investigationId,
        isNewInvestigation,
      },
    });
  }

  clickViewSharePage({
    event,
    shareId,
    sharedBy,
  }: {
    event?: IEvent;
    shareId?: string;
    sharedBy?: string;
  }) {
    // TODO unsure about shareId and sharedBy values
    this.trackEvent({
      ...TRACKING_EVENTS.SAVE_AND_SHARE_VIEW_SHARE,
      properties: {
        ...formatEventObject({ event }),
        shareId,
        sharedBy,
      },
    });
  }

  openDownload({ event }: { event: IEvent }) {
    // Take note of function clickEventMenuViewShare (TRACKING_EVENTS.EVENT_MENU_VIEW_SHARE)
    this.trackEvent({
      ...TRACKING_EVENTS.LOCAL_DOWNLOAD_VIEW,
      properties: {
        ...formatEventObject({ event }),
      },
    });
  }

  // HOME PAGE ////////////////////////////////////////////////////////////////////////////////////////////////////////
  homeWidgetCount({
    total,
    fullListCount,
    pages,
  }: {
    total: number;
    fullListCount: number;
    pages: number;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_COUNT_BY_PAGE,
      properties: {
        total,
        fullListCount,
        pages,
      },
    });
  }

  homeCreateWidgetStart() {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_CREATE_WIDGET_START,
    });
  }

  homeCreateWidget({
    type,
    settings,
  }: {
    type: string;
    settings: {
      description: string;
      locationIds: string[];
      name: string;
      reportTemplateIds: string[];
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_CREATE_WIDGET,
      properties: {
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
      },
    });
  }

  homeDeleteWidget({
    id,
    type,
    settings,
  }: {
    id: string;
    type: string;
    settings: {
      description: string;
      locationIds: string[];
      name: string;
      reportTemplateIds: string[];
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_DELETE_WIDGET,
      properties: {
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
      },
    });
  }

  homeHideWidget({
    id,
    type,
    settings,
  }: {
    id: string;
    type: string;
    settings: {
      description: string;
      locationIds: string[];
      name: string;
      reportTemplateIds: string[];
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_HIDE_WIDGET,
      properties: {
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
      },
    });
  }

  homeShowWidget({
    id,
    type,
    settings,
  }: {
    id: string;
    type: string;
    settings: {
      description: string;
      locationIds: string[]; // TODO: should be updated to proper ID type [in Home.jsx]when passed in (ie. could be SiteId[])
      name: string;
      reportTemplateIds: string[];
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_SHOW_WIDGET,
      properties: {
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
      },
    });
  }

  homeResetWidget({
    id,
    type,
    settings,
    parent,
  }: {
    id: string;
    type: string;
    settings: {
      description: string;
      locationIds: string[]; // TODO: should be updated to proper ID type [in Home.jsx] when passed in (ie. could be SiteId[])
      name: string;
      reportTemplateIds: string[];
    };
    parent: {
      description: string;
      locationIds: string[]; // TODO: should be updated to proper ID type [in Home.jsx] when passed in (ie. could be SiteId[])
      name: string;
      reportTemplateIds: string[];
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_RESET_WIDGET,
      properties: {
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
        ...flattenProps({ prop: 'parent', values: parent }),
      },
    });
  }

  homeSetFilters(filters: {
    description: string;
    locationIds: string[];
    name: string;
    reportTemplateIds: string[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_SET_FILTERS,
      properties: {
        ...flattenProps({ prop: 'filters', values: filters }),
      },
    });
  }

  homePageLoadTime({
    initialLoadTime,
    fullLoadTime,
  }: {
    initialLoadTime: string;
    fullLoadTime: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_PAGE_LOAD_TIME,
      properties: {
        initialLoadTime,
        fullLoadTime,
      },
    });
  }

  homeWidgetLoadTime({
    loadTimeInSeconds,
    id,
    type,
    settings,
    filters,
  }: {
    loadTimeInSeconds: number;
    id: string;
    type: string;
    settings: {
      description: string;
      siteIds: SiteId[];
      name: string;
      reportTemplateIds: string[];
    };
    filters: {
      date: any;
      location: any;
      range: any;
      rangeDetails: any;
    };
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_LOAD_TIME,
      properties: {
        loadTimeInSeconds,
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
        ...flattenProps({ prop: 'filters', values: filters }),
      },
    });
  }

  homeWidgetDrillDown({
    id,
    type,
    destinationPage,
    target,
    filters,
  }: {
    id: string;
    type: string;
    destinationPage: string;
    target: Settings;
    filters: Settings;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_DRILLDOWN,
      properties: {
        id,
        type,
        destinationPage,
        ...flattenProps({ prop: 'target', values: target }),
        ...flattenProps({ prop: 'filters', values: filters }),
      },
    });
  }

  homeWidgetFailedToLoad({
    id,
    type,
    settings,
    filters,
  }: {
    id: string;
    type: string;
    settings: {
      description: string;
      siteIds: SiteId[];
      name: string;
      reportTemplateIds: string[];
    };
    filters: Settings;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_FAILED_TO_LOAD,
      properties: {
        id,
        type,
        ...flattenProps({ prop: 'settings', values: settings }),
        ...flattenProps({ prop: 'filters', values: filters }),
      },
    });
  }

  homeWidgetInvalidLocationIgnored({ id, type, name }: { id: string; type: string; name: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_INVALID_LOCATION_IGNORED,
      properties: {
        id,
        type,
        name,
      },
    });
  }

  homeWidgetDisplayToggle({
    id,
    type,
    name,
    display,
  }: {
    id: string;
    type: string;
    name: string;
    display: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_CHART_TOGGLE,
      properties: {
        id,
        type,
        name,
        display,
      },
    });
  }

  homeReorderWidgets() {
    this.trackEvent({
      ...TRACKING_EVENTS.DASHBOARD_WIDGET_REORDER,
    });
  }

  /**
   * CAMERA GROUPS
   */

  cameraGroupCreated({ group, status }: { group: MinimalGroup | unknown; status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_GROUP_CREATED,
      properties: {
        ...formatGroupObject(group),
        status,
      },
    });
  }

  cameraGroupDeleted({ group, status }: { group: CallhomeCameraGroup; status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_GROUP_DELETED,
      properties: {
        ...formatGroupObject(group),
        status,
      },
    });
  }

  cameraGroupUpdated({
    group,
    status,
    analyticsMetadata,
  }: {
    group: VideoDiscoveryCameraGroup | CallhomeCameraGroup | VideoDiscoverySite;
    status: string;
    analyticsMetadata?: Record<string, unknown>;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CAMERA_GROUP_UPDATED,
      properties: {
        ...(analyticsMetadata ?? {}),
        ...formatGroupObject(group),
        status,
      },
    });
  }

  launchedVideoWall({ source, sourceName }: { source: string; sourceName: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.LAUNCHED_VIDEO_WALL,
      properties: {
        source,
        sourceName,
      },
    });
  }

  /**
   * Arms the specified alarm
   * @param {Object} params
   * @param {string} params.locationId
   * @param {string} params.locationName
   * @param {string} params.status
   * @param {string} params.alarmDescription
   * @param {AlarmConfigState} params.previousAlarmConfigState
   * @param {string} params.siteId
   */
  alarmArm({
    locationId,
    locationName,
    status,
    alarmDescription,
    previousAlarmConfigState,
    siteId,
  }: {
    locationId: string;
    locationName: string;
    status: string;
    alarmDescription: string;
    previousAlarmConfigState: AlarmConfigState;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_ARM,
      properties: {
        alarmConfigState: AlarmConfigState.ARMED_FOREVER,
        locationId,
        locationName,
        status,
        alarmDescription,
        previousAlarmConfigState,
        siteId,
      },
    });
  }

  /**
   * Disarms the specified alarm
   * @param {Object} params
   * @param {string} params.locationId
   * @param {string} params.locationName
   * @param {string} params.status
   * @param {string} params.alarmDescription
   * @param {AlarmConfigState} params.previousAlarmConfigState
   * @param {string} params.siteId
   */
  alarmDisarm({
    locationId,
    locationName,
    status,
    alarmDescription,
    previousAlarmConfigState,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmStatus.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    alarmDescription: string;
    previousAlarmConfigState: AlarmConfigState;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_DISARM,
      properties: {
        alarmConfigState: AlarmConfigState.DISARMED_FOREVER,
        locationId,
        locationName,
        status,
        alarmDescription,
        previousAlarmConfigState,
        siteId,
      },
    });
  }

  alarmSchedule({
    locationId,
    locationName,
    status,
    alarmDescription,
    previousAlarmConfigState,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmStatus.tsx & AlarmConfigurations.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    alarmDescription: string;
    previousAlarmConfigState: AlarmConfigState;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_SCHEDULE,
      properties: {
        alarmConfigState: AlarmConfigState.SCHEDULE,
        locationId,
        locationName,
        status,
        alarmDescription,
        previousAlarmConfigState,
        siteId,
      },
    });
  }

  alarmArmUntil({
    locationId,
    locationName,
    status,
    alarmConfigState,
    armUntil,
    alarmDescription,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmConfigurations.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    alarmDescription: string;
    alarmConfigState: AlarmConfigState;
    armUntil: string | undefined;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_ARM_UNTIL,
      properties: {
        alarmConfigState,
        locationId,
        locationName,
        status,
        armUntil,
        alarmDescription,
        siteId,
      },
    });
  }

  alarmDisarmUntil({
    locationId,
    locationName,
    status,
    alarmConfigState,
    disarmUntil,
    alarmDescription,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmConfigurations.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    alarmDescription: string;
    alarmConfigState: AlarmConfigState;
    disarmUntil: string | undefined;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_DISARM_UNTIL,
      properties: {
        alarmConfigState,
        locationId,
        locationName,
        status,
        disarmUntil,
        alarmDescription,
        siteId,
      },
    });
  }

  alarmMonitoringMode({
    locationId,
    locationName,
    status,
    siteId,
    monitoringMode,
    previousMonitoringMode,
  }: {
    locationId: string;
    locationName: string;
    status: string;
    siteId: SiteId;
    monitoringMode: AlarmMonitoringMode;
    previousMonitoringMode?: AlarmMonitoringMode;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_MONITORING_MODE,
      properties: {
        locationId,
        locationName,
        status,
        siteId,
        monitoringMode,
        previousMonitoringMode,
      },
    });
  }

  alarmCameraEnable({
    locationId,
    locationName,
    status,
    cameraId,
    cameraName,
    siteId,
    deviceId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmCameraConfigModal.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    cameraId: CHCameraId | string;
    cameraName: string;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CAMERA_ENABLE,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        status,
        enable: true,
        siteId,
        deviceId,
      },
    });
  }

  alarmCameraDisable({
    locationId,
    locationName,
    status,
    cameraId,
    cameraName,
    siteId,
    deviceId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmCameraConfigModal.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    cameraId: CHCameraId | string; // TODO: should be updated to proper ID type [in AlarmCameraConfigModal.tsx] when passed in (ie. should be CHCameraId)
    cameraName: string;
    siteId: SiteId;
    deviceId: DeviceId | string; // TODO: should be updated to proper ID type [in AlarmCameraConfigModal.tsx] when passed in (ie. should be DeviceId)
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CAMERA_DISABLE,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        status,
        enable: false,
        siteId,
        deviceId,
      },
    });
  }

  setAlarmCamerasFilter({
    locationId,
    locationName,
    alarmCamerasFilter,
    siteId,
  }: {
    locationId: string;
    locationName: string;
    alarmCamerasFilter: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CAMERAS_FILTER,
      properties: {
        locationId,
        locationName,
        alarmCamerasFilter,
        siteId,
      },
    });
  }

  selectAlarmCameraTag({
    locationId,
    locationName,
    cameraId,
    cameraName,
    tag,
    siteId,
    deviceId,
  }: {
    locationId: string;
    locationName: string;
    cameraId: CHCameraId;
    cameraName: string;
    tag: string | number;
    siteId: SiteId;
    deviceId: DeviceId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SELECT_ALARM_CAMERA_TAG,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        tag,
        siteId,
        deviceId,
      },
    });
  }

  alarmContactAdd(properties: {
    alarmContactName?: string;
    alarmContactEmail?: string | null;
    alarmContactPhoneNumber?: string | null;
    alarmContactLanguagePreference?: string;
    locationId: string;
    siteId: string;
    locationName: string;
    status: string;
    userId?: UserId | null;
    alarmContactType?: AlarmContactType;
    alarmContactPinType?: string;
    alarmContactPriority?: number;
    pinStartsOn?: string;
    pinExpiresOn?: string;
    notes?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CONTACT_ADD,
      properties: {
        ...properties,
        alarmContactType: formatAlarmContactType(properties.alarmContactType),
      },
    });
  }

  alarmContactRemove(properties: {
    locationId: string;
    locationName: string;
    status: string;
    userId?: UserId | null;
    alarmContactType?: AlarmContactType;
    alarmContactPinType?: string;
    alarmContactPriority?: number;
    alarmContactName?: string;
    alarmContactEmail?: string | null;
    alarmContactPhoneNumber?: string | null;
    siteId: string;
    pinStartsOn?: string;
    pinExpiresOn?: string;
    notes?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CONTACT_REMOVE,
      properties: {
        ...properties,
        alarmContactType: formatAlarmContactType(properties.alarmContactType),
      },
    });
  }

  alarmContactUpdate({
    locationId,
    locationName,
    status,
    userId,
    alarmContactType,
    alarmContactPinType,
    alarmContactPriority,
    alarmContactName,
    alarmContactEmail,
    alarmContactPhoneNumber,
    previousAlarmContactPriority,
    previousAlarmContactName,
    previousAlarmContactEmail,
    previousAlarmContactPhoneNumber,
    pinStartsOn,
    pinExpiresOn,
    previousPinStartsOn,
    previousPinExpiresOn,
    siteId,
    notes,
  }: {
    // TODO: simplify these function types if possible
    status: string;
    alarmContactName: string | undefined;
    alarmContactEmail: string | null | undefined;
    alarmContactPhoneNumber: string | null | undefined;
    locationId?: SiteId;
    locationName?: string;
    userId?: UserId | null;
    alarmContactType: AlarmContactType | undefined;
    alarmContactPinType?: string;
    alarmContactPriority: number | undefined;
    previousAlarmContactPriority?: number | undefined;
    previousAlarmContactName?: string | undefined;
    previousAlarmContactEmail?: string | null | undefined;
    previousAlarmContactPhoneNumber?: string | null | undefined;
    pinStartsOn?: string;
    pinExpiresOn?: string;
    previousPinStartsOn?: string;
    previousPinExpiresOn?: string;
    siteId?: SiteId;
    notes?: string;
  }) {
    const properties: {
      locationId?: SiteId;
      locationName?: string;
      status: string;
      userId?: UserId | null;
      alarmContactName?: string;
      alarmContactEmail?: string | null | undefined;
      alarmContactPhoneNumber?: string | null | undefined;
      alarmContactType?: string;
      alarmContactPinType?: string;
      alarmContactPriority?: number;
      siteId?: SiteId;
      previousAlarmContactPriority?: number;
      previousAlarmContactName?: string;
      previousAlarmContactEmail?: string | null;
      previousAlarmContactPhoneNumber?: string | null;
      pinStartsOn?: string;
      pinExpiresOn?: string;
      previousPinStartsOn?: string;
      previousPinExpiresOn?: string;
      notes?: string;
    } = {
      locationId,
      locationName,
      status,
      userId,
      alarmContactName,
      alarmContactEmail,
      alarmContactPhoneNumber,
      alarmContactType: formatAlarmContactType(alarmContactType),
      alarmContactPinType,
      alarmContactPriority,
      siteId,
      notes,
      ...(pinStartsOn ? { pinStartsOn } : {}),
      ...(pinExpiresOn ? { pinExpiresOn } : {}),
    };

    // Only add previous fields if they are different than the current ones
    if (previousAlarmContactPriority !== alarmContactPriority) {
      properties.previousAlarmContactPriority = previousAlarmContactPriority;
    }
    if (previousAlarmContactName !== alarmContactName) {
      properties.previousAlarmContactName = previousAlarmContactName;
    }
    if (previousAlarmContactEmail !== alarmContactEmail) {
      properties.previousAlarmContactEmail = previousAlarmContactEmail;
    }
    if (previousAlarmContactPhoneNumber !== alarmContactPhoneNumber) {
      properties.previousAlarmContactPhoneNumber = previousAlarmContactPhoneNumber;
    }
    if (previousPinStartsOn && previousPinStartsOn !== pinStartsOn) {
      properties.previousPinStartsOn = previousPinStartsOn;
    }
    if (previousPinExpiresOn && previousPinExpiresOn !== pinExpiresOn) {
      properties.previousPinExpiresOn = previousPinExpiresOn;
    }

    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CONTACT_UPDATE,
      properties,
    });
  }

  // phase 3 cleanup (SL-16229): unused with the migrated customer alarm-contact schema;
  // delete with the legacy AlarmPinPanel / DTMFPIN path.
  alarmPinAdd(properties: {
    name: string;
    alarmContactLanguagePreference?: string;
    locationId: SiteId;
    siteId: SiteId;
    siteName: string;
    status: string;
    userId?: UserId | null;
    alarmContactType: AlarmContactType;
    disarmFor: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_PIN_ADD,
      properties,
    });
  }

  // phase 3 cleanup (SL-16229): unused with the migrated customer alarm-contact schema;
  // delete with the legacy AlarmPinPanel / DTMFPIN path.
  alarmPinRemove(properties: {
    locationId: SiteId;
    siteName: string;
    status: string;
    userId?: UserId | null;
    alarmContactType: AlarmContactType;
    name: string;
    siteId: SiteId;
    disarmFor: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_PIN_REMOVE,
      properties,
    });
  }

  // phase 3 cleanup (SL-16229): unused with the migrated customer alarm-contact schema;
  // delete with the legacy AlarmPinPanel / DTMFPIN path.
  alarmPinUpdate({
    locationId,
    siteName,
    status,
    userId,
    alarmContactType,
    name,
    previousAlarmContactName,
    siteId,
    disarmFor,
    previousDisarmFor,
  }: {
    status: string;
    name: string;
    locationId?: SiteId;
    siteName?: string;
    userId?: UserId | null;
    alarmContactType: AlarmContactType;
    previousAlarmContactName?: string | undefined;
    siteId?: SiteId;
    disarmFor: string;
    previousDisarmFor?: string;
  }) {
    const properties: {
      locationId?: SiteId;
      siteName?: string;
      status: string;
      userId?: UserId | null;
      name: string;
      alarmContactType: AlarmContactType;
      siteId?: SiteId;
      previousAlarmContactName?: string;
      disarmFor: string;
      previousDisarmFor?: string;
    } = {
      locationId,
      siteName,
      status,
      userId,
      name,
      alarmContactType,
      siteId,
      disarmFor,
    };

    if (previousAlarmContactName !== name) {
      properties.previousAlarmContactName = previousAlarmContactName;
    }
    if (previousDisarmFor !== disarmFor) {
      properties.previousDisarmFor = previousDisarmFor;
    }

    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_PIN_UPDATE,
      properties,
    });
  }

  alarmPinView({
    alarmContactType,
    ...rest
  }: {
    name: string;
    alarmContactLanguagePreference?: string;
    locationId: SiteId;
    siteId: SiteId;
    locationName: string;
    status: string;
    userId?: UserId | null;
    alarmContactType: AlarmContactType;
    disarmFor: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_PIN_VIEW,
      properties: {
        ...rest,
        alarmContactType: formatAlarmContactType(alarmContactType),
      },
    });
  }

  alarmScheduledExceptionAdd(properties: { status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_SCHEDULED_EXCEPTION_ADD,
      properties,
    });
  }

  scheduledSpotCheckCreated(properties: { status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SCHEDULED_SPOT_CHECK_CREATED,
      properties,
    });
  }

  scheduledSpotCheckUpdated(properties: {
    previousSpotCheckStartTime: string;
    previousSpotCheckEndTime?: string;
    previousSpotCheckRepeat: string;
    previousOpenAIChatPrompt: string;
    previousDeviceId?: DeviceId;
    previousPromptSource: AIInspectionDeviceType;
    status: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SCHEDULED_SPOT_CHECK_UPDATED,
      properties,
    });
  }

  spotCheckPreviewCreated(properties: { status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SCHEDULED_SPOT_CHECK_PREVIEW_CREATED,
      properties,
    });
  }

  alarmScheduledExceptionUpdate({
    alarmExceptionStartTime,
    alarmExceptionEndTime,
    alarmExceptionRepeat,
    alarmExceptionTitle,
    alarmExceptionDescription,
    alarmExceptionType,
    previousAlarmExeptionStartTime,
    previousAlarmExceptionEndTime,
    previousAlarmExceptionRepeat,
    previousAlarmExceptionTitle,
    previousAlarmExceptionDescription,
    previousAlarmExceptionType,
    status,
    locationId,
    locationName,
    siteId,
  }: {
    alarmExceptionStartTime: string;
    alarmExceptionEndTime: string;
    alarmExceptionRepeat: string;
    alarmExceptionTitle: string;
    alarmExceptionDescription: string;
    alarmExceptionType: TypeOfAlarmException;
    previousAlarmExeptionStartTime?: string;
    previousAlarmExceptionEndTime?: string;
    previousAlarmExceptionRepeat?: string;
    previousAlarmExceptionTitle?: string;
    previousAlarmExceptionDescription?: string;
    previousAlarmExceptionType?: TypeOfAlarmException;
    status: string;
    locationId: string; // TODO: should be updated to proper ID type [in ScheduledActivityForm.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    siteId: SiteId | string; // TODO: should be updated to proper ID type [in ScheduledActivityForm.tsx] when passed in (ie. should be SiteId)
  }) {
    const properties: {
      alarmExceptionStartTime: string;
      alarmExceptionEndTime: string;
      alarmExceptionRepeat: string;
      alarmExceptionTitle: string;
      alarmExceptionDescription: string;
      alarmExceptionType: TypeOfAlarmException;
      status: string;
      locationId: string;
      locationName: string;
      siteId: SiteId | string;
      previousAlarmExeptionStartTime?: string;
      previousAlarmExceptionEndTime?: string;
      previousAlarmExceptionRepeat?: string;
      previousAlarmExceptionTitle?: string;
      previousAlarmExceptionDescription?: string;
      previousAlarmExceptionType?: TypeOfAlarmException;
    } = {
      alarmExceptionStartTime,
      alarmExceptionEndTime,
      alarmExceptionRepeat,
      alarmExceptionTitle,
      alarmExceptionDescription,
      alarmExceptionType,
      status,
      locationId,
      locationName,
      siteId,
    };

    // Only add previous fields if they are different than the current ones
    if (previousAlarmExeptionStartTime !== alarmExceptionStartTime) {
      properties.previousAlarmExeptionStartTime = previousAlarmExeptionStartTime;
    }
    if (previousAlarmExceptionEndTime !== alarmExceptionEndTime) {
      properties.previousAlarmExceptionEndTime = previousAlarmExceptionEndTime;
    }
    if (previousAlarmExceptionRepeat !== alarmExceptionRepeat) {
      properties.previousAlarmExceptionRepeat = previousAlarmExceptionRepeat;
    }
    if (previousAlarmExceptionTitle !== alarmExceptionTitle) {
      properties.previousAlarmExceptionTitle = previousAlarmExceptionTitle;
    }
    if (previousAlarmExceptionDescription !== alarmExceptionDescription) {
      properties.previousAlarmExceptionDescription = previousAlarmExceptionDescription;
    }
    if (previousAlarmExceptionType !== alarmExceptionType) {
      properties.previousAlarmExceptionType = previousAlarmExceptionType;
    }

    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_SCHEDULED_EXCEPTION_UPDATE,
      properties,
    });
  }

  alarmScheduledExceptionDelete(properties: { status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_SCHEDULED_EXCEPTION_DELETE,
      properties,
    });
  }

  alarmGuestAccessAdd({
    locationId,
    locationName,
    status,
    expiryDate,
    actionType,
    siteId,
  }: {
    locationId: string;
    locationName: string;
    status: string;
    expiryDate: string;
    actionType: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_GUEST_ACCESS_ADD,
      properties: {
        actionType, // single | bulk
        locationId,
        locationName,
        status,
        expiryDate,
        siteId,
      },
    });
  }

  alarmGuestAccessResend({
    locationId,
    locationName,
    status,
    expiryDate,
    actionType,
    siteId,
  }: {
    locationId: SiteId | undefined;
    locationName: string;
    status: string;
    expiryDate: string;
    actionType: string;
    siteId: SiteId | undefined;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_GUEST_ACCESS_RESEND,
      properties: {
        actionType, // single | bulk
        locationId,
        locationName,
        status,
        expiryDate,
        siteId,
      },
    });
  }

  alarmGuestAccessRevoke({
    locationId,
    locationName,
    status,
    actionType,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in usePINAccess.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    actionType: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_GUEST_ACCESS_REVOKE,
      properties: {
        actionType, // single | bulk
        locationId,
        locationName,
        status,
        siteId,
      },
    });
  }

  alarmGuestAccessRemove({
    locationId,
    locationName,
    status,
    actionType,
    siteId,
  }: {
    locationId: SiteId | undefined;
    locationName: string;
    status: string;
    actionType: string;
    siteId: SiteId | undefined;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_GUEST_ACCESS_REMOVE,
      properties: {
        actionType, // single | bulk
        locationId,
        locationName,
        status,
        siteId,
      },
    });
  }

  alarmScheduleUpdate({
    alarmConfigState,
    locationId,
    locationName,
    previousSchedule,
    schedule,
    status,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmStatus.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    alarmConfigState: AlarmConfigState;
    previousSchedule: string;
    schedule: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_SCHEDULE_UPDATE,
      properties: {
        alarmConfigState,
        locationId,
        locationName,
        previousSchedule,
        schedule,
        status,
        siteId,
      },
    });
  }

  alarmModeUpdate({
    locationId,
    locationName,
    status,
    mode,
    siteId,
  }: {
    locationId: string; // TODO: should be updated to proper ID type [in AlarmConfigurations.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    mode: AlarmMode;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_MODE_UPDATE,
      properties: {
        locationId,
        locationName,
        status,
        mode,
        siteId,
      },
    });
  }

  alarmNoteUpdate({
    // not currentlyused anywhere
    locationId,
    locationName,
    status,
    note,
    siteId,
  }: {
    locationId: string;
    locationName: string;
    status: string;
    note: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_NOTE_UPDATE,
      properties: {
        locationId,
        locationName,
        status,
        note,
        siteId,
      },
    });
  }

  alarmKeyValueUpdate({
    locationId,
    locationName,
    status,
    key,
    value,
    siteId,
  }: {
    locationId: SiteId;
    locationName: string;
    status: Status;
    key: string;
    value: string | undefined;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_KEY_VALUE_UPDATE,
      properties: {
        locationId,
        locationName,
        status,
        [key]: value,
        siteId,
      },
    });
  }

  alarmClose({
    eventId,
    locationId,
    locationName,
    status,
    siteId,
  }: {
    eventId: string; // TODO: should be updated to proper ID type [in AlarmEventActions.tsx] when passed in (ie. could be EventId)
    locationId: string; // TODO: should be updated to proper ID type [in AlarmEventActions.tsx] when passed in (ie. could be SiteId)
    locationName: string;
    status: string;
    siteId: string | SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_CLOSE,
      properties: {
        eventId,
        locationId,
        locationName,
        status,
        siteId,
      },
    });
  }

  alarmMotionZoneUpdate({
    alarmConfigState,
    cameraName,
    locationId,
    locationName,
    numberOfGridSpacesHighlighted,
    previousNumberOfGridSpacesHighlighted,
    status,
    siteId,
    deviceId,
    initiatedFrom,
    description,
  }: {
    locationId: string;
    locationName: string;
    status: string;
    alarmConfigState: AlarmConfigState;
    cameraName: string;
    numberOfGridSpacesHighlighted: number;
    previousNumberOfGridSpacesHighlighted: number;
    siteId: SiteId;
    deviceId: DeviceId;
    initiatedFrom: string;
    description?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ALARM_MOTION_ZONE_UPDATE,
      properties: {
        alarmConfigState,
        cameraName,
        locationId,
        locationName,
        numberOfGridSpacesHighlighted,
        previousNumberOfGridSpacesHighlighted,
        status,
        siteId,
        deviceId,
        initiatedFrom,
      },
      description,
    });
  }

  /**
   * Thresholds
   */
  thresholdCreated({
    id,
    type,
    ownerType,
    settings,
    thresholds,
  }: {
    id: string;
    type: string;
    ownerType: string;
    settings: Settings;
    thresholds: Threshold[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.THRESHOLD_CREATE,
      properties: { id, type, ownerType, settings, thresholds },
    });
  }

  thresholdUpdated({
    id,
    type,
    ownerType,
    settings,
    thresholds,
  }: {
    id: string;
    type: string;
    ownerType: string;
    settings: Settings;
    thresholds: Threshold[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.THRESHOLD_UPDATE,
      properties: { id, type, ownerType, settings, thresholds },
    });
  }

  thresholdDeleted({
    id,
    type,
    ownerType,
    settings,
    thresholds,
  }: {
    id: string;
    type: string;
    ownerType: string;
    settings: Settings;
    thresholds: Threshold[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.THRESHOLD_DELETE,
      properties: { id, type, ownerType, settings, thresholds },
    });
  }

  // MISCELLANEOUS ////////////////////////////////////////////////////////////////////////////////////////////////////
  showKeyboardShortcuts() {
    this.trackEvent({
      ...TRACKING_EVENTS.SHOW_KEYBOARD_SHORTCUTS,
    });
  }

  visitFeatureTab(feature?: string) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FEATURE,
      properties: {
        feature,
      },
    });
  }

  selectedLocationIdNotAvailableKiosk() {
    this.trackEvent({
      ...TRACKING_EVENTS.SELECTED_LOCATION_ID_NOT_AVAILABLE,
    });
  }

  callLocationsApi({
    numberOfLocations,
    durationOfNetworkRequest,
    statusCode,
    url,
    sizeOfResponseBody,
  }: {
    numberOfLocations?: number;
    durationOfNetworkRequest: string;
    statusCode: string;
    url: string;
    sizeOfResponseBody?: number;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.LOCATIONS_API,
      properties: {
        numberOfLocations,
        durationOfNetworkRequest,
        sizeOfResponseBody,
        statusCode,
        url,
      },
    });
  }

  createTestAlarm(properties: { status: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CREATE_TEST_ALARM,
      properties,
    });
  }

  sendAPIRequest(properties: { URL: string; method: string; source: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SEND_API_REQUEST,
      properties,
    });
  }

  assignLocationTagsToLocation(properties: {
    status: string;
    location: string;
    locationTags?: string[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ASSIGN_LOCATION_TAGS_TO_LOCATION,
      properties,
    });
  }

  updateLocationTagCategories(properties: { status: string; tagCategories: string[] }) {
    this.trackEvent({
      ...TRACKING_EVENTS.UPDATE_LOCATION_TAG_CATEGORIES,
      properties,
    });
  }

  bulkAssignLocationTags(properties: { status: string; locationTags: string[] }) {
    this.trackEvent({
      ...TRACKING_EVENTS.BULK_ASSIGN_LOCATION_TAGS,
      properties,
    });
  }

  selectLocations(properties: {
    locations: {
      id: SiteId;
      name: string | undefined;
    }[];
    locationTags?: readonly string[];
    context?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SELECT_LOCATIONS,
      properties,
    });
  }

  /**
   * Video Control Option Toggles
   */
  toggledPinholeView({ showPinholeView }: { showPinholeView: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_PINHOLE_VIEW,
      properties: {
        showPinholeView,
      },
    });
  }

  toggledLimitEventDuration({ limitEventDuration }: { limitEventDuration: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_LIMIT_EVENT_DURATION,
      properties: {
        limitEventDuration,
      },
    });
  }

  fetching24HourAlarmStatsError({ error, pollingRate }: { error: object; pollingRate: string }) {
    this.trackEvent({
      ...TRACKING_EVENTS.FETCHING_24_HOUR_ALARM_STATS_ERROR,
      properties: {
        ...error,
        pollingRate,
      },
    });
  }

  displayEventDataAsDateTime({
    fieldId,
    fieldType,
    value,
  }: {
    fieldId: string;
    fieldType: Field['type'];
    value: number | string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.RENDER_EVENT_DATA_AS_DATE_TIME,
      properties: {
        fieldId,
        fieldType,
        value,
      },
    });
  }

  createRiskFlag({
    name,
    type,
    deviceTags,
    outlier,
    weight,
    isSuccess,
    ownerType,
  }: {
    name: string;
    type: string;
    deviceTags: string[];
    outlier: string;
    weight: number;
    isSuccess: boolean;
    ownerType: CallHomeWidget['ownerType'];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CREATE_RISK_FLAG,
      properties: {
        name,
        type,
        deviceTags,
        outlier,
        weight,
        ownerType,
        isSuccess,
      },
    });
  }

  updateRiskFlag({
    id,
    name,
    payload,
    isSuccess,
  }: {
    id: string;
    name: string;
    payload: object;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.UPDATE_RISK_FLAG,
      properties: {
        id,
        name,
        isSuccess,
        ...payload,
      },
    });
  }

  deleteRiskFlag({ name, id, isSuccess }: { id: string; name: string; isSuccess: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.DELETE_RISK_FLAG,
      properties: {
        name,
        id,
        isSuccess,
      },
    });
  }

  eventVerify({
    id = '',
    isSuccess,
    conclusion = '',
    conclusionReason = '',
    verifiedBy,
    schemaId,
    workflowId,
    workflowName,
  }: {
    id?: string;
    isSuccess: boolean;
    conclusion?: string;
    conclusionReason?: string;
    verifiedBy: string;
    schemaId: string;
    workflowId: string;
    workflowName?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_VERIFY,
      properties: {
        id,
        isSuccess,
        conclusion,
        conclusionReason,
        verifiedBy,
        schemaId,
        workflowId,
        workflowName,
      },
    });
  }

  eventFlowCreated({
    workflowId,
    workflowName,
    eventType = '',
    isSuccess,
  }: {
    workflowId?: string;
    workflowName: string;
    eventType?: string;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FLOW_CREATED,
      properties: {
        workflowId,
        workflowName,
        eventType,
        isSuccess,
      },
    });
  }

  eventFlowUpdated({
    workflowId,
    workflowName,
    isSuccess,
  }: {
    workflowId: string;
    workflowName: string;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FLOW_UPDATED,
      properties: {
        workflowId,
        workflowName,
        isSuccess,
      },
    });
  }

  eventFlowDeleted({
    workflowId,
    workflowName,
    isSuccess,
  }: {
    workflowId: string;
    workflowName: string;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FLOW_DELETED,
      properties: {
        workflowId,
        workflowName,
        isSuccess,
      },
    });
  }

  eventFlowTagRemoved({
    workflowId,
    workflowName,
    isSuccess,
    tag,
  }: {
    workflowId?: string;
    workflowName?: string;
    isSuccess: boolean;
    tag?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EVENT_FLOW_TAG_REMOVED,
      properties: {
        workflowId,
        workflowName,
        tag,
        isSuccess,
      },
    });
  }

  aiVideoToolClick({
    tool,
    playerType,
    cameraId,
    cameraName,
  }: {
    tool?: string;
    playerType: PlayerType;
    cameraId: CHCameraId;
    cameraName: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.AI_VIDEO_TOOL_CLICK,
      properties: {
        tool,
        playerType,
        cameraId,
        cameraName,
      },
    });
  }

  createLineCrossEventLines({
    names,
    types,
    enterLabels,
    exitLabels,
    generateOccupancy,
    cameraId, // the CH unique camera ID
    cameraName,
    isSuccess,
  }: {
    names: string[];
    types: ('in_out' | 'left_right' | 'up_down')[];
    enterLabels: ('in' | 'left' | 'up')[];
    exitLabels: ('right' | 'out' | 'down')[];
    generateOccupancy: boolean[];
    cameraId: CHCameraId | string;
    cameraName: string;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.CREATE_LINE_CROSS_EVENT_LINES,
      properties: {
        names,
        types,
        enterLabels,
        exitLabels,
        generateOccupancy,
        cameraId,
        cameraName,
        isSuccess,
      },
    });
  }

  editLineCrossEventLines({
    names,
    types,
    enterLabels,
    exitLabels,
    generateOccupancy,
    cameraId, // the CH unique camera ID
    cameraName,
    isSuccess,
  }: {
    names: string[];
    types: ('in_out' | 'left_right' | 'up_down')[];
    enterLabels: ('in' | 'left' | 'up')[];
    exitLabels: ('right' | 'out' | 'down')[];
    generateOccupancy: boolean[];
    cameraId: CHCameraId | string; // TODO: should be updated to proper ID type [in useLineCrossEventsLine.ts] when passed in (ie. ideally should just be CHCameraId)
    cameraName: string;
    isSuccess: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.EDIT_LINE_CROSS_EVENT_LINES,
      properties: {
        names,
        types,
        enterLabels,
        exitLabels,
        generateOccupancy,
        cameraId,
        cameraName,
        isSuccess,
      },
    });
  }

  accessLineCrossEventTab({
    source,
    cameraId,
    cameraName,
  }: {
    source: string;
    cameraId?: string; //TODO: should be updated to proper ID type when passed in (ie. CHCameraId)
    cameraName?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ACCESS_LINE_CROSS_EVENT_TAB,
      properties: {
        source, // 'tab' | 'configure from search'
        cameraId,
        cameraName,
      },
    });
  }

  setCloudStorageCameraRetentionPackage(properties: {
    location: string;
    camera: string;
    streamId: string;
    cameraId: string; // TODO: should be updated to proper ID type [in CloudVideoBackupBreakdown.tsx] when passed in (ie. could be CHCameraId)
    stream: string;
    quality: number;
    retentionPeriod?: number;
    estimatedStorage?: number;
    siteBandwidth: number;
    availableBandwidth: number;
    cloudStoragePackage: number;
    type: string;
    differenceInRetentionDays: number;
    derivedStreams: DerivedStream[];
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SET_CLOUD_STORAGE_CAMERA_RETENTION_PACKAGE,
      properties,
    });
  }

  cloudStorageUpgradeRequested() {
    this.trackEvent({
      ...TRACKING_EVENTS.CLOUD_STORAGE_UPGRADE_REQUESTED,
    });
  }

  setSupportAccess({
    isSupportAccessEnabled,
    supportAccessExpiresAt,
    supportAccessDuration,
  }: {
    isSupportAccessEnabled: boolean;
    supportAccessExpiresAt: number;
    supportAccessDuration?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.SET_SUPPORT_ACCESS,
      properties: {
        isSupportAccessEnabled,
        supportAccessExpiresAt,
        supportAccessDuration,
      },
    });
  }

  toggledShowZoneTrackingZone({ showZoneTrackingZone }: { showZoneTrackingZone: boolean }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_SHOW_ZONE_TRACKING_ZONE,
      properties: {
        showZoneTrackingZone,
      },
    });
  }

  toggledHighlightObjects({
    showHighlightObjects,
    playerType,
    cameraIds,
    playbackTime,
  }: {
    showHighlightObjects: boolean;
    playerType: PlayerType;
    cameraIds: string[];
    playbackTime?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_HIGHLIGHT_OBJECTS,
      properties: {
        showHighlightObjects,
        interactionSource: 'toggle',
        playerType,
        cameraIds,
        cameraCount: cameraIds.length,
        playbackTime,
      },
    });
  }
  toggledHighlightObjectsExtendedInfo({
    showExtendedInfo,
    playerType,
  }: {
    showExtendedInfo: boolean;
    playerType: PlayerType;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.TOGGLED_HIGHLIGHT_OBJECTS_EXTENDED_INFO,
      properties: {
        showExtendedInfo,
        interactionSource: 'hotkey',
        playerType,
      },
    });
  }

  /**
   * @typedef {Object} FieldStatsLoadTimeProperties
   * @property {number} timeInSeconds - The time it took to load the field stats
   * @property {string} context - The context of the field stats load. e.g. 'login' | `feature:${FeatureTab}`
   * @property {boolean} isQuick - Whether the field stats were from index mappings (true) or from the searching (false)
   */
  fieldStatsLoadTime({
    timeInSeconds,
    context,
    isQuick,
  }: {
    timeInSeconds: number;
    context?: FieldStatsContext;
    isQuick?: boolean;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.FIELD_STATS_LOAD_TIME,
      properties: {
        timeInSeconds,
        context,
        isQuick,
      },
    });
  }

  // toggle on whether this is sending Search Feedback OR Query
  /**
   * @typedef {Object} VideoSearchFeedbackProperties
   * @property {boolean | undefined} positiveFeedback - Whether the feedback was positive or negative
   * @property {string} query - The search query that was used
   * @property {'text' | 'image'} mediaType - Whether the search used text or an image
   * @property {'text' | 'upload' | 'video player crop'} mediaMethod - How the search media was provided
   * @property {number} timeRangeStart - The start time of the search range in milliseconds
   * @property {number} timeRangeEnd - The end time of the search range in milliseconds
   * @property {SiteId[]} siteId - The siteIds of the search
   * @property {eventType} isQuery - this is the feedback type being sent
   * @property {semanticUri} semanticUri - is null or string giving information of embedded search
   * @property {semanticUri} semanticTs - the start time of the semantic event
   * @property {userInteraction} userInteraction - true if user clicked on at least one search result
   */
  feedbackVideoSearch({
    positiveFeedback,
    query,
    mediaType,
    mediaMethod,
    timeRangeStart,
    timeRangeEnd,
    siteId,
    precision,
    customerId,
    isQuery,
    semanticUri,
    semanticTs,
    userInteraction,
    savedClip,
  }: {
    positiveFeedback: boolean | undefined;
    query: string;
    mediaType: ValueOf<typeof VIDEO_SEARCH_MEDIA_TYPE>;
    mediaMethod: ValueOf<typeof VIDEO_SEARCH_MEDIA_METHOD>;
    timeRangeStart: number;
    timeRangeEnd: number;
    siteId: readonly string[];
    precision: Precision;
    customerId: readonly CustomerId[];
    isQuery: boolean;
    semanticUri: string | null;
    semanticTs: number | null;
    userInteraction: boolean;
    savedClip: boolean;
  }) {
    let feedback = 'unknown';
    if (typeof positiveFeedback === 'boolean') {
      // Convert boolean to string for consistency
      feedback = positiveFeedback ? 'yes' : 'no';
    }

    const mixPanelEventType = isQuery
      ? TRACKING_EVENTS.VIDEO_SEARCH_QUERY
      : savedClip
        ? TRACKING_EVENTS.VIDEO_SEARCH_SAVED_CLIP
        : TRACKING_EVENTS.VIDEO_SEARCH_FEEDBACK;

    return this.trackEvent({
      ...mixPanelEventType,
      properties: {
        feedback,
        query,
        mediaType,
        mediaMethod,
        searchTime: new Date().toISOString(),
        timeRangeStart,
        timeRangeEnd,
        siteId,
        precision,
        customerId,
        semanticUri,
        semanticTs,
        userInteraction,
      },
    });
  }

  trackVideoSearchUpload({
    fileName,
    contentType,
    size,
  }: {
    fileName: string;
    contentType: string;
    size: number;
  }) {
    return this.trackEvent({
      ...TRACKING_EVENTS.VIDEO_SEARCH_UPLOAD,
      properties: {
        fileName,
        contentType,
        size,
        mediaType: VIDEO_SEARCH_MEDIA_TYPE.IMAGE,
        mediaMethod: VIDEO_SEARCH_MEDIA_METHOD.UPLOAD,
      },
    });
  }

  saveZoneTrackingConfig({
    cameraId,
    cameraName,
    locationId,
    oldZoneCount,
    newZoneCount,
    oldZones,
    newZones,
    maxOccupancyEnabledZones = [],
    saveBatchId,
  }: {
    cameraId: string;
    cameraName?: string;
    locationId: string;
    oldZoneCount: number;
    newZoneCount: number;
    oldZones: EventTrackingZone[];
    newZones: EventTrackingZone[];
    maxOccupancyEnabledZones?: Array<{
      zoneId: string;
      zoneName: string;
      zoneType: string;
      objectKinds?: string[];
      count?: number;
      gracePeriodMs?: number;
    }>;
    /**
     * Optional correlation id — when the caller emits per-zone
     * `zone-tracking-config-saved-zone` events for the same save, they all
     * share this id so downstream analysts can reconstruct the full batch.
     */
    saveBatchId?: string;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.ZONE_TRACKING_CONFIG_SAVED,
      properties: {
        cameraId,
        cameraName,
        locationId,
        oldZoneCount,
        newZoneCount,
        oldZones,
        newZones,
        hasMaxOccupancyEnabled: maxOccupancyEnabledZones.length > 0,
        maxOccupancyEnabledZoneCount: maxOccupancyEnabledZones.length,
        maxOccupancyEnabledZones,
        saveBatchId,
      },
    });
  }

  /**
   * Emits one event per max-occupancy state transition detected between the
   * old and new zone snapshots (enabled, disabled, or modified-while-enabled).
   * No-op transitions are filtered out. All emitted events share `saveBatchId`
   * with the aggregate `zone-tracking-config-saved` event so they can be
   * correlated downstream.
   */
  emitZoneMaxOccupancyChangedEvents(args: {
    cameraId: string;
    cameraName?: string;
    locationId: string;
    saveBatchId: string;
    oldZones: EventTrackingZone[];
    newZones: EventTrackingZone[];
  }) {
    const events = buildZoneMaxOccupancyChangedEvents(args);
    for (const properties of events) {
      this.trackEvent({
        ...TRACKING_EVENTS.ZONE_MAX_OCCUPANCY_CHANGED,
        properties,
      });
    }
  }

  trackSDMEvent(
    eventName: string,
    properties: { description: string; [key: string]: string | boolean | number | undefined },
  ) {
    const event = {
      ...TRACKING_EVENTS.SDM_EVENT,
      name: eventName,
    };

    this.trackEvent({
      ...event,
      properties: { ...properties },
    });
  }

  ptzEnable({
    cameraId,
    cameraName,
    locationId,
    locationName,
    siteId,
  }: {
    cameraId: CHCameraId | string;
    cameraName: string;
    locationId: string;
    locationName: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PTZ_ENABLE,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        siteId,
      },
    });
  }

  ptzDisable({
    cameraId,
    cameraName,
    locationId,
    locationName,
    siteId,
  }: {
    cameraId: CHCameraId | string;
    cameraName: string;
    locationId: string;
    locationName: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PTZ_DISABLE,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        siteId,
      },
    });
  }

  ptzMove({
    cameraId,
    cameraName,
    locationId,
    locationName,
    siteId,
  }: {
    cameraId: CHCameraId | string;
    cameraName: string;
    locationId: string;
    locationName: string;
    siteId: SiteId;
  }) {
    this.trackEvent({
      ...TRACKING_EVENTS.PTZ_MOVE,
      properties: {
        cameraId,
        cameraName,
        locationId,
        locationName,
        siteId,
      },
    });
  }
}

export default new AnalyticsLogger();
